import { useState, useRef, useCallback, useEffect } from 'react';
import { graphBuilderApi } from '../../../services/api';

const POLL_INTERVAL = 1500;
const REPORT_POLL_INTERVAL = 3000;
const REPORT_POLL_MAX_ATTEMPTS = 20;

/**
 * Custom hook for managing a single A/B execution lifecycle:
 *   start -> poll status -> collect results -> poll report.
 *
 * Uses chained setTimeout (not setInterval) so async callbacks
 * never overlap, preventing leaked intervals.
 */
export default function useABExecution() {
  const [executionId, setExecutionId] = useState(null);
  const [executionState, setExecutionState] = useState(null);
  const [nodeResults, setNodeResults] = useState(null);
  const [reportStatus, setReportStatus] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const reportPollRef = useRef(null);
  const reportPollCountRef = useRef(0);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    if (reportPollRef.current) { clearTimeout(reportPollRef.current); reportPollRef.current = null; }
    reportPollCountRef.current = 0;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const startReportPolling = useCallback((execId) => {
    reportPollCountRef.current = 0;
    const tick = async () => {
      reportPollCountRef.current += 1;
      if (reportPollCountRef.current > REPORT_POLL_MAX_ATTEMPTS) {
        reportPollRef.current = null;
        return;
      }
      try {
        const rs = await graphBuilderApi.getReportStatus(execId);
        if (!mountedRef.current) return;
        setReportStatus(rs);
        if (rs.available) {
          reportPollRef.current = null;
          return;
        }
      } catch (_) { /* ignore */ }
      if (mountedRef.current) {
        reportPollRef.current = setTimeout(tick, REPORT_POLL_INTERVAL);
      }
    };
    reportPollRef.current = setTimeout(tick, REPORT_POLL_INTERVAL);
  }, []);

  const pollStatus = useCallback((execId) => {
    const tick = async () => {
      try {
        const state = await graphBuilderApi.getProcessStatus(execId);
        if (!mountedRef.current) return;
        setExecutionState(state);

        const terminal = ['completed', 'failed', 'cancelled'];
        if (terminal.includes(state.status)) {
          pollRef.current = null;
          setIsRunning(false);

          try {
            const results = await graphBuilderApi.getNodeResults(execId);
            if (mountedRef.current) setNodeResults(results);
          } catch (_) { /* best effort */ }

          startReportPolling(execId);
          return;
        }
      } catch (e) {
        console.error('Status poll error', e);
      }
      if (mountedRef.current) {
        pollRef.current = setTimeout(tick, POLL_INTERVAL);
      }
    };
    pollRef.current = setTimeout(tick, POLL_INTERVAL);
  }, [startReportPolling]);

  const startExecution = useCallback(async ({
    processId,
    input,
    inputs: explicitInputs,
    workspaceId,
    nodeOverrides,
    abSessionId,
    tabLabel,
  }) => {
    setError(null);
    setIsRunning(true);
    setNodeResults(null);
    setReportStatus(null);
    stopPolling();

    try {
      const resolvedInputs = explicitInputs || { case_data: input };
      const resp = await graphBuilderApi.executeProcessAB({
        process_id: processId,
        inputs: resolvedInputs,
        execution_mode: 'full',
        workspace_id: workspaceId || null,
        node_overrides: nodeOverrides || null,
        ab_session_id: abSessionId || null,
        tab_label: tabLabel || null,
      });

      const execId = resp.execution_id;
      if (!mountedRef.current) return null;
      setExecutionId(execId);
      setExecutionState(resp);
      pollStatus(execId);
      return { executionId: execId, abSessionId: resp.ab_session_id || abSessionId };
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (mountedRef.current) {
        setError(msg);
        setIsRunning(false);
      }
      return null;
    }
  }, [pollStatus, stopPolling]);

  const reconnect = useCallback(async (execId) => {
    if (!execId) return;
    setError(null);
    setNodeResults(null);
    setReportStatus(null);
    stopPolling();
    setExecutionId(execId);

    try {
      const state = await graphBuilderApi.getProcessStatus(execId);
      if (!mountedRef.current) return;
      setExecutionState(state);

      const terminal = ['completed', 'failed', 'cancelled'];
      if (!terminal.includes(state.status)) {
        setIsRunning(true);
        pollStatus(execId);
      } else {
        setIsRunning(false);

        try {
          const results = await graphBuilderApi.getNodeResults(execId);
          if (mountedRef.current) setNodeResults(results);
        } catch (_) { /* best effort */ }

        try {
          const rs = await graphBuilderApi.getReportStatus(execId);
          if (!mountedRef.current) return;
          setReportStatus(rs);
          if (rs.generating) {
            startReportPolling(execId);
          }
        } catch (_) { /* best effort */ }
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e?.response?.data?.detail || e.message || 'Failed to load execution');
      }
    }
  }, [pollStatus, stopPolling, startReportPolling]);

  const cancelExecution = useCallback(async () => {
    if (!executionId) return;
    try {
      const resp = await graphBuilderApi.cancelProcessExecution(executionId);
      stopPolling();
      if (mountedRef.current) {
        setExecutionState(resp);
        setIsRunning(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      if (mountedRef.current) setError(msg);
    }
  }, [executionId, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setExecutionId(null);
    setExecutionState(null);
    setNodeResults(null);
    setReportStatus(null);
    setIsRunning(false);
    setError(null);
  }, [stopPolling]);

  return {
    executionId,
    executionState,
    nodeResults,
    reportStatus,
    isRunning,
    error,
    startExecution,
    cancelExecution,
    reconnect,
    reset,
    stopPolling,
  };
}
